import React from 'react';
import { get } from 'lodash';
import { Maybe, Promotion, Benefit, SubBenefitType, BillingFrequency } from '@css-xp/graphtypes';
import { FormattedDate } from '../../formatted';
import { PromotionDuration } from '@atlassian/cssxp-types-promotion';
import { getBenefitValueWithSubBenefitType } from '../../helper/src/promotion-utils';

enum INVALID_PROMO_TYPE {
  NOT_AVAILABLE = 'Not available',
}

export enum SUB_BENEFIT_TYPE {
  TRIAL = 'TRIAL',
  FLAT = 'FLAT',
}

export const getReadablePromotionTitle = (promotion: Maybe<Promotion>) => {
  return <span>{promotion?.title || INVALID_PROMO_TYPE.NOT_AVAILABLE}</span>;
};

export const getCSVReadablePromotionTitle = (promotion: Maybe<Promotion>): string => {
  return promotion?.title || INVALID_PROMO_TYPE.NOT_AVAILABLE;
};

export const getFlatDiscountBenefitText = (promotion: Maybe<Promotion>, hasHyphenSeparator = true) => {
  const benefit = promotion?.benefits?.[0];
  const currency = get(promotion, `eligibilityRules.currency[${0}]`, '');
  let promoBenefits = '';
  if (benefit?.duration === PromotionDuration.REPEATING) {
    if (benefit?.iterations) {
      promoBenefits =
        benefit?.iterations === 1 ? `${benefit?.iterations} Billing cycle` : `${benefit?.iterations} Billing cycles`;
    }
  } else {
    promoBenefits = benefit?.duration ?? '';
  }

  //@ts-ignore
  const value = getBenefitValueWithSubBenefitType(benefit, currency);
  const benefitText = `${currency} ${value}discount` + (hasHyphenSeparator ? ' - ' : ', ') + promoBenefits;

  return benefitText;
};

export const getReadablePromotionValue = (
  promotion: Maybe<Promotion>,
  hasHyphenSeparator = true,
  isFlatDiscountSupportEnabled = false
) => {
  if (!promotion) {
    return INVALID_PROMO_TYPE.NOT_AVAILABLE;
  }

  if (promotion.benefits?.[0]?.subBenefitType === SUB_BENEFIT_TYPE.TRIAL) {
    return promotion.benefits[0].value + ' Days (' + promotion.benefits[0].subBenefitType + ')';
  }

  if (isFlatDiscountSupportEnabled && promotion.benefits?.[0]?.subBenefitType === SUB_BENEFIT_TYPE.FLAT) {
    const benefitText = getFlatDiscountBenefitText(promotion);
    return <span>{benefitText}</span>;
  }

  const benefitLength = promotion.benefits?.length ?? 0;
  return promotion.benefits?.map((benefit: Maybe<Benefit>, index: number) => {
    const promoBenefits =
      benefit?.duration === PromotionDuration.REPEATING
        ? benefit?.iterations + ' Billing cycles'
        : benefit?.duration ?? '';
    const lowerCaseSubBenefitType = benefit?.subBenefitType?.toLowerCase() ?? '';
    const subBenefitTypeText =
      SubBenefitType?.PERCENTAGE?.toLowerCase() === lowerCaseSubBenefitType
        ? '% Discount' + (hasHyphenSeparator ? ' - ' : ', ') + promoBenefits
        : ` (${lowerCaseSubBenefitType})`;

    return (
      <React.Fragment key={index}>
        <span>
          {(benefit?.value && subBenefitTypeText && `${benefit?.value}${subBenefitTypeText}`) ??
            INVALID_PROMO_TYPE.NOT_AVAILABLE}
        </span>
        {index !== benefitLength - 1 && <span>,</span>}
      </React.Fragment>
    );
  });
};

export const getCSVReadablePromotionValue = (
  promotion: Maybe<Promotion>,
  hasHyphenSeparator = true,
  isFlatDiscountSupportEnabled?: boolean
): string => {
  if (!promotion) {
    return INVALID_PROMO_TYPE.NOT_AVAILABLE;
  }

  if (promotion.benefits?.[0]?.subBenefitType === SUB_BENEFIT_TYPE.TRIAL) {
    return `${promotion.benefits[0].value} Days (${promotion.benefits[0].subBenefitType})`;
  }

  if (isFlatDiscountSupportEnabled && promotion.benefits?.[0]?.subBenefitType === SUB_BENEFIT_TYPE.FLAT) {
    const benefitText = getFlatDiscountBenefitText(promotion);
    return benefitText;
  }

  const benefits = promotion.benefits?.reduce((acc: string, benefit: Maybe<Benefit>) => {
    const promoBenefits =
      benefit?.duration === PromotionDuration.REPEATING
        ? `${benefit?.iterations} Billing cycles`
        : benefit?.duration ?? '';
    const lowerCaseSubBenefitType = benefit?.subBenefitType?.toLowerCase() ?? '';
    const subBenefitTypeText =
      SubBenefitType?.PERCENTAGE?.toLowerCase() === lowerCaseSubBenefitType
        ? `% Discount${hasHyphenSeparator ? ' - ' : ', '}${promoBenefits}`
        : ` (${lowerCaseSubBenefitType})`;

    const benefitString =
      (benefit?.value && subBenefitTypeText ? `${benefit?.value}${subBenefitTypeText}` : null) ??
      INVALID_PROMO_TYPE.NOT_AVAILABLE;
    return acc ? `${acc}, ${benefitString}` : benefitString;
  }, '');

  return benefits ?? '';
};

export const getReadablePromotionWindow = (
  promotion: Maybe<Promotion>,
  promotionStartDate: number | undefined | null = null,
  promotionFrequency: string | null = null
) => {
  if (promotionStartDate) {
    const periodStartdate = new Date(promotionStartDate);
    const promotionIterations = promotion?.benefits?.reduce(
      (filterValue, next) =>
        (filterValue = (filterValue > (next?.iterations ? next?.iterations : 1) ? filterValue : next?.iterations) ?? 1),
      1
    );
    const promotionEndDate =
      promotionFrequency && promotionFrequency === BillingFrequency.ANNUAL
        ? new Date(periodStartdate.setFullYear(periodStartdate.getFullYear() + (promotionIterations ?? 1)))
        : new Date(periodStartdate.setMonth(periodStartdate.getMonth() + (promotionIterations ?? 1)));
    return (
      <>
        <FormattedDate date={promotionStartDate.toString()} /> to{' '}
        <FormattedDate date={promotionEndDate.getTime()?.toString()} />{' '}
      </>
    );
  }
  return (
    <span>
      {promotion?.promotionWindow?.startTime ? (
        <FormattedDate date={promotion.promotionWindow.startTime} />
      ) : (
        INVALID_PROMO_TYPE.NOT_AVAILABLE
      )}
      {promotion?.promotionWindow?.endTime ? (
        <>
          {' - '}
          <FormattedDate date={promotion.promotionWindow.endTime} />
        </>
      ) : (
        INVALID_PROMO_TYPE.NOT_AVAILABLE
      )}
    </span>
  );
};

export const getReadableDiscountValue = (promotion: Maybe<Promotion>) => {
  if (!promotion || !promotion?.benefits) {
    return INVALID_PROMO_TYPE.NOT_AVAILABLE;
  }

  if (promotion.benefits?.[0]?.subBenefitType === SUB_BENEFIT_TYPE.TRIAL) {
    return promotion.benefits[0].value + ' Days (' + promotion.benefits[0].subBenefitType + ')';
  }

  const benefitLength = promotion.benefits?.length ?? 0;
  return promotion.benefits?.map((benefit: Maybe<Benefit>, index: number) => {
    const lowerCaseSubBenefitType = benefit?.subBenefitType?.toLowerCase() ?? '';
    const subBenefitTypeText =
      SubBenefitType?.PERCENTAGE?.toLowerCase() === lowerCaseSubBenefitType ? '%' : ` (${lowerCaseSubBenefitType})`;

    return (
      <>
        <span key={index}>
          {(benefit?.value && subBenefitTypeText && `${benefit?.value}${subBenefitTypeText}`) ??
            INVALID_PROMO_TYPE.NOT_AVAILABLE}
        </span>
        {index !== benefitLength - 1 && <span key={index}>,</span>}
      </>
    );
  });
};

export const getReadableDurationValue = (promotion: Maybe<Promotion>) => {
  if (!promotion || !promotion?.benefits) {
    return INVALID_PROMO_TYPE.NOT_AVAILABLE;
  }

  const benefitLength = promotion.benefits?.length;
  return promotion.benefits?.map((benefit: Maybe<Benefit>, index: number) => {
    return (
      <>
        <span key={index}>{(benefit?.duration && `${benefit?.duration}`) ?? INVALID_PROMO_TYPE.NOT_AVAILABLE}</span>
        {index !== benefitLength - 1 && <span key={index}>,</span>}
      </>
    );
  });
};
